import pipelineKeys from '@/api/queries/pipelines/keyFactory';
import { GetPipelineQuery } from '@/gql/graphql';
import {
  MutationKey,
  UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { DRAFT_MUTATION_SCOPE_ID } from '../../context/DraftModeContext';
import { SquashConfig, trySquashPreviousMutation } from './squash';

type MutationContext<TExtraContext> = TExtraContext & {
  previousPipelineQuery?: GetPipelineQuery;
};

type DraftPipelineMutationConfig<TData, TVariables, TExtraContext> = {
  mutationKey: MutationKey;
  mutationFn: (variables: TVariables) => Promise<TData>;
  applyOptimisticUpdate: (
    data: GetPipelineQuery,
    variables: TVariables,
    extraContext: TExtraContext
  ) => GetPipelineQuery;
  onBeforeOptimisticUpdate?: (variables: TVariables) => TExtraContext;
  squash?: SquashConfig<TVariables>;
};

/**
 * Factory for draft pipeline mutation hooks that operate on the GetPipelineQuery cache.
 * Handles the shared boilerplate: scoping, cancel-on-mutate, optimistic update, and rollback.
 *
 * When `squash` is configured, consecutive mutations of the same type targeting the same
 * entity are merged into one, reducing API calls when the queue is flushed.
 *
 * Usage without extra context: `createDraftPipelineMutation()({ mutationKey, mutationFn, applyOptimisticUpdate })`
 * Usage with extra context:    `createDraftPipelineMutation<{ publicId: string }>()({ mutationKey, mutationFn, applyOptimisticUpdate })`
 */
export function createDraftPipelineMutation<
  TExtraContext extends Record<string, unknown> = Record<string, never>,
>() {
  return <TData, TVariables>(
    config: DraftPipelineMutationConfig<TData, TVariables, TExtraContext>
  ) => {
    return (args?: {
      pipelineId?: string;
      options?: UseMutationOptions<TData, unknown, TVariables>;
    }) => {
      const queryClient = useQueryClient();

      return useMutation<
        TData,
        unknown,
        TVariables,
        MutationContext<TExtraContext>
      >({
        ...args?.options,
        mutationKey: config.mutationKey,
        mutationFn: config.mutationFn,
        scope: { id: DRAFT_MUTATION_SCOPE_ID },
        onMutate: async (variables) => {
          // --- Squash-on-enqueue ---
          let inheritedPreviousPipelineQuery: GetPipelineQuery | undefined;

          if (config.squash) {
            const result = trySquashPreviousMutation({
              mutationCache: queryClient.getMutationCache(),
              mutationKey: config.mutationKey,
              variables,
              squash: config.squash,
            });

            // Mutate in-place: TanStack Query shares one `variables` reference
            // between onMutate and mutationFn (via closure), with no public API
            // to replace it, so Object.assign is the only way to propagate
            // merged variables through to the mutation function.
            if (result.mergedVariables) {
              Object.assign(
                variables as Record<string, unknown>,
                result.mergedVariables
              );
            }
            inheritedPreviousPipelineQuery =
              result.inheritedPreviousPipelineQuery;
          }

          const extraContext =
            config.onBeforeOptimisticUpdate?.(variables) ??
            ({} as TExtraContext);

          if (!args?.pipelineId) {
            void args?.options?.onMutate?.(variables);
            return { ...extraContext };
          }

          await queryClient.cancelQueries({
            queryKey: pipelineKeys.pipeline(args.pipelineId),
          });

          const previousPipelineQuery =
            inheritedPreviousPipelineQuery ??
            queryClient.getQueryData<GetPipelineQuery>(
              pipelineKeys.pipeline(args.pipelineId)
            );

          const currentPipelineQuery =
            queryClient.getQueryData<GetPipelineQuery>(
              pipelineKeys.pipeline(args.pipelineId)
            );

          if (currentPipelineQuery) {
            queryClient.setQueryData<GetPipelineQuery>(
              pipelineKeys.pipeline(args.pipelineId),
              config.applyOptimisticUpdate(
                currentPipelineQuery,
                variables,
                extraContext
              )
            );
          }

          void args?.options?.onMutate?.(variables);
          return {
            ...extraContext,
            previousPipelineQuery,
          };
        },
        onError: (err, variables, context) => {
          const previousPipelineQuery = context?.previousPipelineQuery;

          if (previousPipelineQuery && args?.pipelineId) {
            queryClient.setQueryData(
              pipelineKeys.pipeline(args.pipelineId),
              previousPipelineQuery
            );
          }

          args?.options?.onError?.(err, variables, context);
        },
      });
    };
  };
}
